import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';
import {
  CustomerUpdateData,
  ICustomer,
} from '../../model/customer';
import { ICustomersApiService } from './customers-api.interface';

import { environment } from '@orderna/admin-panel/src/environments/environment';
import {
  fromCustomerDetailsDtoMapper,
  fromUpdateCustomerDtoMapper,
} from '../mapper/customers.mapper';
import { SuccessResponse } from '../shared/success-response';
import { FindCustomersQueryDto } from './dtos/find-customers.query.dto';
import { FindCustomersResponseDto } from './dtos/find-customers.response.dto';
import { FindCustomerEditFormDataResponseDto } from './dtos/find-customer-edit-form-data.response.dto';
import { HandleBlobHttpResponseOptions } from '@orderna/admin-panel/src/types/handle-blob-http-response-options';
import { handleBlobHttpResponse } from '@orderna/admin-panel/src/utils/handle-blob-http-response';
import { ExportCustomersQueryDto } from './dtos/export-customers.query.dto';
import { FindCustomerDetailsResponseDto } from './dtos/find-customer-details.response.dto';
import { FindCustomerDetailsQueryDto } from './dtos/find-customer-details.query.dto';
import { FindCustomerProfileResponseDto } from './dtos/partials/find-customer-profile.response.dto';
import { FindCustomerTransactionStatsResponseDto } from './dtos/partials/find-customer-transaction-stats.response.dto';
import { FindCustomerPosSummaryResponseDto } from './dtos/find-customer-pos-summary.response.dto';
import { FindCustomerStatutoryDiscountsResponseDto } from './dtos/find-customer-statutory-discounts.response.dto';
import { FindCustomerBnpcStatusResponseDto } from './dtos/find-customer-bnpc-status.response.dto';
import { ExportExcelPdfService } from '../export-excel-pdf/export-excel-pdf.service';
import { FindCustomerNamesQueryDto } from './dtos/find-customer-names.query.dto';
import { FindCustomerNamesResponseDto } from './dtos/find-customer-names.response.dto';
import { FindCustomerByStatutoryDiscountQueryDto } from './dtos/find-customer-by-statutory-discount.query.dto';
import { FindCustomerByStatutoryDiscountResponseDto } from './dtos/find-customer-by-statutory-discount.response.dto';
import { CreateCustomerRequestDto } from './dtos/create-customer.request.dto';
import { CreateCustomerResponseDto } from './dtos/create-customer.response.dto';

@Injectable({
  providedIn: 'root',
})
export class CustomersApiService implements ICustomersApiService {
  private http = inject(HttpClient);
  private exportService = inject(ExportExcelPdfService);
  private readonly API_URL = `${environment.api}/customers`;

  async findCustomers(
    query: FindCustomersQueryDto,
  ): Promise<FindCustomersResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomersResponseDto>>(this.API_URL, {
        params,
      }),
    );

    return response.data;
  }

  async findCustomerByStatutoryDiscount(
    query: FindCustomerByStatutoryDiscountQueryDto,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<
        SuccessResponse<FindCustomerByStatutoryDiscountResponseDto>
      >(`${this.API_URL}/by-statutory-discount`, { params }),
    );

    return response.data;
  }

  async findCustomerEditFormData(customerId: number) {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomerEditFormDataResponseDto>>(
        `${this.API_URL}/${customerId}/edit`,
      ),
    );

    return response.data;
  }

  async findCustomerDetails(
    customerId: number,
    query: FindCustomerDetailsQueryDto,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomerDetailsResponseDto>>(
        `${this.API_URL}/${customerId}/details`,
        { params },
      ),
    );

    return response.data;
  }

  async findCustomerProfile(
    customerId: number,
  ): Promise<FindCustomerProfileResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomerProfileResponseDto>>(
        `${this.API_URL}/${customerId}/profile`,
      ),
    );

    return response.data;
  }

  async findCustomerTransactionStats(
    customerId: number,
  ): Promise<FindCustomerTransactionStatsResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomerTransactionStatsResponseDto>>(
        `${this.API_URL}/${customerId}/transaction-stats`,
      ),
    );

    return response.data;
  }

  async findCustomerStatutoryDiscounts(
    customerId: number,
  ): Promise<FindCustomerStatutoryDiscountsResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomerStatutoryDiscountsResponseDto>>(
        `${this.API_URL}/${customerId}/statutory-discounts`,
      ),
    );

    return response.data;
  }

  async findCustomerNames(
    query: FindCustomerNamesQueryDto,
  ): Promise<FindCustomerNamesResponseDto> {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get<SuccessResponse>(`${this.API_URL}/names`, { params }),
    );

    return response.data;
  }

  async findCustomerPosSummary(
    customerId: number,
  ): Promise<FindCustomerPosSummaryResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomerPosSummaryResponseDto>>(
        `${this.API_URL}/${customerId}/pos-summary`,
      ),
    );

    return response.data;
  }

  async findCustomerBnpcStatus(
    customerId: number,
  ): Promise<FindCustomerBnpcStatusResponseDto> {
    const response = await firstValueFrom(
      this.http.get<SuccessResponse<FindCustomerBnpcStatusResponseDto>>(
        `${this.API_URL}/${customerId}/bnpc-status`,
      ),
    );

    return response.data;
  }

  getCustomer(customerId: ICustomer['id']): Observable<any> {
    const result = this.http
      .get(`${this.API_URL}/${customerId}`)
      .pipe(map(this.mapCustomerDetailResponse));
    return result;
  }

  async createCustomer(data: CreateCustomerRequestDto) {
    const response = await firstValueFrom(
      this.http.post<SuccessResponse<CreateCustomerResponseDto>>(
        `${this.API_URL}`,
        data
      )
    );

    return response.data;
  }

  updateCustomer({ id, ...data }: CustomerUpdateData): Observable<any> {
    const result = this.http
      .patch(`${this.API_URL}/${id}`, data)
      .pipe(map(this.mapUpdateCustomerResponse));
    return result;
  }

  deleteCustomer(customerId: ICustomer['id']): Observable<any> {
    return this.http.delete(`${this.API_URL}/${customerId}`);
  }

  deleteCustomers(customerIds: ICustomer['id'][]): Observable<any> {
    return this.http.post(`${this.API_URL}/delete-many`, {
      customerIds: customerIds,
    });
  }

  async exportCustomers(
    query: ExportCustomersQueryDto,
    options?: HandleBlobHttpResponseOptions,
  ) {
    const params = new HttpParams({ fromObject: query as any });
    const response = await firstValueFrom(
      this.http.get(`${this.API_URL}/export`, {
        responseType: 'blob',
        observe: 'response',
        params,
      }),
    );

    handleBlobHttpResponse(response, options, this.exportService);
  }

  private mapCustomerDetailResponse(response: any): any {
    const mappedData = fromCustomerDetailsDtoMapper(response);
    return {
      ...response,
      data: mappedData,
    };
  }

  private mapUpdateCustomerResponse(response: any): any {
    const mapped = fromUpdateCustomerDtoMapper(response.data);
    return {
      ...response,
      data: mapped,
    };
  }
}
